import { OverflowBadge } from '@dealstack/frontend/app/pages/Valuations/components/OverflowBadgeList/OverflowBadge';
import {
	GAP,
	estimateElementMaxAvailableSpace,
	maxBadgeCountInContainer,
	measureBadgeWidth,
} from '@dealstack/frontend/app/pages/Valuations/components/OverflowBadgeList/OverflowBadgeList.utils';

import { defaultTheme } from '@dealstack/frontend/shared/mechanism/styleguide/defaultTheme';
import { TEST_ELEMENT_IDS } from '@dealstack/shared/tests/testElementIds';
import { Flex, Menu, Stack, rem, MenuDropdownProps } from '@mantine/core';
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { BadgeListItem } from './OverflowBadgeList.utils.types';

interface Props {
	badges: BadgeListItem[];
	onBadgeRemove?: (id: string) => void;
}

const BadgeListDropdown = styled(Menu.Dropdown)<MenuDropdownProps>`
	padding: 0.5rem;
	${defaultTheme.shadow.z1}
`;

export function OverflowBadgeList({ badges, onBadgeRemove }: Props) {
	const containerRef = useRef<HTMLDivElement>(null);
	const badgeRefs = useRef<HTMLDivElement[]>([]);
	const fontRef = useRef<HTMLDivElement>(null);

	const [viewableBadges, setViewableBadges] = useState<BadgeListItem[]>(badges);
	const [overflowedBadges, setOverflowedBadges] = useState<BadgeListItem[]>([]);
	const [dropdownOpened, setDropdownOpened] = useState(false);

	const calculateOverflow = useCallback(() => {
		if (!containerRef.current) {
			return;
		}

		const container = containerRef.current;
		const { maxWidth, maxHeight } = estimateElementMaxAvailableSpace(container);

		const badgeWidths = badges.map((badge) =>
			measureBadgeWidth({
				ref: fontRef,
				label: badge.label,
				hasRemove: !!onBadgeRemove,
				hasIcon: !!badge.iconVariant,
			})
		);
		const maxBadgeCount = maxBadgeCountInContainer(badgeWidths, maxHeight, maxWidth);
		if (maxBadgeCount === badges.length) {
			setViewableBadges(badges);
			setOverflowedBadges([]);
			return;
		}

		let count = maxBadgeCount;
		for (; count > 0; count--) {
			const overflowCountBadgeWidth = measureBadgeWidth({
				ref: fontRef,
				label: `+${count}`,
				hasRemove: false,
				hasIcon: false,
			});
			const partialBadgeWidths = [...badgeWidths.slice(0, count), overflowCountBadgeWidth];

			if (
				maxBadgeCountInContainer(partialBadgeWidths, maxHeight, maxWidth) ===
				partialBadgeWidths.length
			) {
				break;
			}
		}

		const visibleBadges = badges.slice(0, count);
		const hiddenBadges = badges.slice(count);

		setViewableBadges(visibleBadges);
		setOverflowedBadges(hiddenBadges);
	}, [badges, onBadgeRemove]);

	const handleBadgeRemove = useCallback(
		(id: string) => {
			onBadgeRemove?.(id);
			calculateOverflow();
		},
		[onBadgeRemove, calculateOverflow]
	);

	useLayoutEffect(() => {
		calculateOverflow();
	}, [calculateOverflow]);

	useEffect(() => {
		if (!containerRef.current?.parentElement) {
			return;
		}

		const resizeObserver = new ResizeObserver(() => {
			calculateOverflow();
		});

		resizeObserver.observe(containerRef.current.parentElement);

		return () => {
			resizeObserver.disconnect();
		};
	}, [calculateOverflow]);

	return (
		<Flex
			data-testid={TEST_ELEMENT_IDS.OVERFLOW_BADGE_LIST}
			ref={containerRef}
			gap={rem(4)}
			wrap="wrap"
			style={{ maxHeight: '100%', maxWidth: '100%' }}
		>
			{viewableBadges.map((badge, index) => (
				<OverflowBadge
					ref={(el) => {
						if (el) {
							badgeRefs.current[index] = el;
						}
					}}
					key={`${badge.id}-viewable`}
					badge={badge}
					onRemove={onBadgeRemove ? handleBadgeRemove : undefined}
				/>
			))}
			{overflowedBadges.length > 0 && (
				<Menu
					shadow="md"
					position="bottom-start"
					withinPortal
					opened={dropdownOpened}
					onClose={() => setDropdownOpened(false)}
				>
					<Menu.Target>
						<Stack
							onClick={() => setDropdownOpened(!dropdownOpened)}
							style={{ cursor: 'pointer' }}
						>
							<OverflowBadge
								style={{ userSelect: 'none' }}
								badge={{ id: 'overflow', label: `+${overflowedBadges.length}` }}
							/>
						</Stack>
					</Menu.Target>
					<BadgeListDropdown>
						<div style={{ display: 'flex', flexDirection: 'column', gap: rem(GAP) }}>
							{overflowedBadges.map((badge) => (
								<OverflowBadge
									key={`${badge.id}-overflow`}
									badge={badge}
									onRemove={onBadgeRemove ? handleBadgeRemove : undefined}
								/>
							))}
						</div>
					</BadgeListDropdown>
				</Menu>
			)}
			{/* only used for font measuring */}
			<OverflowBadge
				ref={fontRef}
				badge={{ id: 'font-measure', label: '' }}
				style={{ display: 'none' }}
			/>
		</Flex>
	);
}
